import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useInfiniteArchivedSearch } from '@/hooks/use-search';
import { useSearchSort } from '@/hooks/use-search-sort';
import { useRangeSelection } from '@/hooks/use-range-selection';
import { useWorkspaceSort } from '@/hooks/use-workspace-sort';
import { useArchiveColumns } from '@/hooks/use-archive-columns';
import { useArchiveSelection } from '@/hooks/use-archive-selection';
import { useSmartArchive, useSmartDelete } from '@/hooks/rxdb-smart';

import { BackTo } from '@/components/back-to';
import { VirtualTable } from '@/components/virtual-table';
import { ArchiveActionAlert } from '@/features/archive/archive-action-alert';
import { ArchiveFilters, type ArchiveTypeId } from '@/components/archive-filters';

import { emptyLayoutAtom } from '@/state';
import type { SearchableDocument } from '@/services/search-service/orama-schema';

import { Alert } from '@flintmint/web-shared/components/alert';
import { Button } from '@flintmint/web-shared/components/button';
import { Collapse } from '@flintmint/web-shared/components/collapse';
import { Icon } from '@flintmint/web-shared/components/icon';
import { NoData } from '@flintmint/web-shared/components/no-data';

import styles from './archive.module.scss';

export const Archive = () => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedType, setSelectedType] = useState<ArchiveTypeId>('all');

  const { restore, empty } = useSmartArchive();
  const { delete: smartDelete } = useSmartDelete();
  const { sortParams, onSort } = useWorkspaceSort();
  const { isSearching, menuSort, querySort, setSearchSort } = useSearchSort(searchValue, sortParams);

  const { data, isLoading, hasMore, isLoadingMore, loadMore } = useInfiniteArchivedSearch({
    term: searchValue,
    sort: querySort,
    where: {
      types: selectedType !== 'all' ? selectedType : ['notes', 'templates', 'blocks', 'userSources'],
    },
  });

  const items = useMemo(() => data.map((hit) => hit.document), [data]);
  const resetKey = useMemo(
    () => JSON.stringify({ term: searchValue, type: selectedType, sort: querySort }),
    [searchValue, selectedType, querySort]
  );
  const selection = useArchiveSelection(items);
  const isArchiveEmpty = !items.length && !searchValue && selectedType === 'all';
  const [isEmptying, setIsEmptying] = useState(false);

  const getSelections = useCallback(() => selection.selectedItems, [selection.selectedItems]);
  const setSelections = useCallback(
    (next: SearchableDocument[]) => {
      selection.setSelectedItems(next);
    },
    [selection.setSelectedItems]
  );

  const range = useRangeSelection({
    items,
    getSelections,
    setSelections,
    toggleOnPlainClick: true,
  });

  const clearSelection = useCallback(() => {
    selection.clearSelection();
    range.resetRangeState();
  }, [selection.clearSelection, range.resetRangeState]);

  const toggleAllVisible = useCallback(() => {
    selection.toggleAllVisible();
    range.resetRangeState();
  }, [selection.toggleAllVisible, range.resetRangeState]);

  const onArchiveListEndReached = useCallback(
    (visible: boolean) => {
      if (visible && hasMore && !isLoadingMore) {
        loadMore();
      }
    },
    [hasMore, isLoadingMore, loadMore]
  );

  const setEmptyLayout = useSetAtom(emptyLayoutAtom);

  useEffect(() => {
    setEmptyLayout(false);
  }, [setEmptyLayout]);

  const restoreItems = useCallback(
    (docs: SearchableDocument[]) => {
      return restore(docs);
    },
    [restore]
  );

  const deleteItems = useCallback(
    (docs: SearchableDocument[]) => {
      return smartDelete(docs);
    },
    [smartDelete]
  );

  const restoreItem = useCallback(
    (item: SearchableDocument) => restoreItems([item]).then(() => selection.deselectItem(item)),
    [restoreItems, selection.deselectItem]
  );

  const deleteItem = useCallback(
    (item: SearchableDocument) => deleteItems([item]).then(() => selection.deselectItem(item)),
    [deleteItems, selection.deselectItem]
  );

  const columns = useArchiveColumns({
    selection: {
      selectedIds: selection.selectedIds,
      allVisibleSelected: selection.allVisibleSelected,
      someVisibleSelected: selection.someVisibleSelected,
      toggleAllVisible,
      toggleItem: range.toggleItem,
    },
    onRestoreItem: restoreItem,
    onDeleteItem: deleteItem,
  });

  const restoreSelections = useCallback(() => {
    return restoreItems(selection.selectedItems).then(clearSelection);
  }, [restoreItems, selection.selectedItems, clearSelection]);

  const deleteSelections = useCallback(() => {
    return deleteItems(selection.selectedItems).then(clearSelection);
  }, [deleteItems, selection.selectedItems, clearSelection]);

  const emptyArchive = useCallback(async () => {
    setIsEmptying(true);
    try {
      await empty();
      clearSelection();
    } finally {
      setIsEmptying(false);
    }
  }, [empty, clearSelection]);

  return (
    <>
      <header className={styles.archiveHeader}>
        <BackTo />
        <div className={styles.archiveHeading}>
          <div>
            <h1>Archive</h1>
            <p>Restore archived items or remove them from Archive.</p>
          </div>
          <Alert
            type="danger"
            popoverProps={{
              offset: [0, 16],
            }}
            maxWidth={350}
            title={<>Empty Archive?</>}
            message="Are you sure you want to empty the Archive? Items in the Archive will be permanently deleted. This action cannot be undone."
            primaryButton={{
              children: 'Empty Archive',
              loading: isEmptying,
              onClick: () => {
                void emptyArchive();
              },
            }}
            trigger={
              <Button
                disabled={isArchiveEmpty || isEmptying}
                loading={isEmptying}
                cornerRadius="smooth"
                color="danger"
                appearance="greyscale"
              >
                Empty Archive
              </Button>
            }
          />
        </div>
      </header>
      <ArchiveFilters
        onSort={onSort}
        menuSort={menuSort}
        searchValue={searchValue}
        isSearching={isSearching}
        selectedType={selectedType}
        onSearchSort={setSearchSort}
        onSearchChange={setSearchValue}
        onSelectedTypeChange={setSelectedType}
      />
      <Collapse open={selection.hasSelections} unmountOnCollapse className={styles.selectionBar}>
        <span>{selection.selectedCount} selected</span>
        <ArchiveActionAlert action="restore" count={selection.selectedCount} onConfirm={restoreSelections}>
          <Button cornerRadius="smooth" color="hero">
            Restore
          </Button>
        </ArchiveActionAlert>
        <ArchiveActionAlert action="delete" count={selection.selectedCount} onConfirm={deleteSelections}>
          <Button cornerRadius="smooth" color="danger" appearance="outline">
            Delete from Archive
          </Button>
        </ArchiveActionAlert>
        <Button appearance="clear" color="dark" cornerRadius="smooth" onClick={clearSelection}>
          Clear selection
        </Button>
      </Collapse>
      <section className={styles.archiveContent}>
        {isLoading || items.length ? (
          <VirtualTable
            items={items}
            columns={columns}
            estimateSize={72}
            loading={isLoading}
            loadingMore={isLoadingMore}
            resetKey={resetKey}
            selections={selection.selectedItems}
            disableRowStates={false}
            onLastItemVisible={onArchiveListEndReached}
            endReachRows={2}
            rowProps={(item) => ({
              onClick: (event) => range.onItemClick(event, item),
            })}
          />
        ) : (
          <NoData
            className={styles.archiveNoData}
            color="darker"
            description={
              searchValue || selectedType !== 'all'
                ? 'We couldn’t find anything by your request,\n' + 'try to search something else  or change the filters.'
                : 'No archived items'
            }
            icon={<Icon multiColor type="delete-03-twotone" size={48} />}
          />
        )}
      </section>
    </>
  );
};
